import { atom } from "jotai"
import { store } from "@/store/store"
export { getMaskGroupDigest } from "@/core/utils/mask-group-digest"

export interface MaskGroupThumbnailCache {
	photoId: string | null
	thumbnails: Record<string, string>
}

const pendingMaskGroupThumbnailKeys = new Set<string>()
const failedMaskGroupThumbnailKeys = new Set<string>()

export const maskGroupThumbnailCacheAtom = atom<MaskGroupThumbnailCache>({
	photoId: null,
	thumbnails: {},
})

function getMaskGroupThumbnailCacheKey(
	photoId: string,
	groupId: string,
	transformKey: string,
	maskGroupDigest: string,
) {
	return `${photoId}:${groupId}:${transformKey}:${maskGroupDigest}`
}

function revokeThumbnailUrl(url: string) {
	if (url.startsWith("blob:") && typeof URL.revokeObjectURL === "function") {
		URL.revokeObjectURL(url)
	}
}

export function hasMaskGroupThumbnail(
	cache: MaskGroupThumbnailCache,
	photoId: string,
	groupId: string,
	transformKey: string,
	maskGroupDigest: string,
) {
	const key = getMaskGroupThumbnailCacheKey(photoId, groupId, transformKey, maskGroupDigest)
	if (pendingMaskGroupThumbnailKeys.has(key) || failedMaskGroupThumbnailKeys.has(key)) {
		return true
	}

	if (cache.photoId !== photoId) {
		return false
	}

	return Object.hasOwn(cache.thumbnails, key)
}

export function getMaskGroupThumbnail(
	cache: MaskGroupThumbnailCache,
	photoId: string,
	groupId: string,
	transformKey: string,
	maskGroupDigest: string,
) {
	return cache.thumbnails[
		getMaskGroupThumbnailCacheKey(photoId, groupId, transformKey, maskGroupDigest)
	]
}

export function setMaskGroupThumbnail(
	photoId: string,
	groupId: string,
	transformKey: string,
	maskGroupDigest: string,
	thumbnailUrl: string,
) {
	const key = getMaskGroupThumbnailCacheKey(photoId, groupId, transformKey, maskGroupDigest)
	pendingMaskGroupThumbnailKeys.delete(key)
	const groupPrefix = `${photoId}:${groupId}:`
	const cache = store.get(maskGroupThumbnailCacheAtom)
	for (const [cachedKey, cachedUrl] of Object.entries(cache.thumbnails)) {
		if (
			(cache.photoId !== photoId || cachedKey.startsWith(groupPrefix)) &&
			cachedUrl !== thumbnailUrl
		) {
			revokeThumbnailUrl(cachedUrl)
		}
	}
	for (const failedKey of failedMaskGroupThumbnailKeys) {
		if (failedKey.startsWith(groupPrefix)) {
			failedMaskGroupThumbnailKeys.delete(failedKey)
		}
	}

	store.set(maskGroupThumbnailCacheAtom, (cache) => {
		const thumbnails = cache.photoId === photoId ? cache.thumbnails : {}
		const nextThumbnails = Object.fromEntries(
			Object.entries(thumbnails).filter(([key]) => !key.startsWith(groupPrefix)),
		)
		return {
			photoId,
			thumbnails: {
				...nextThumbnails,
				[key]: thumbnailUrl,
			},
		}
	})
}

export function setMaskGroupThumbnailBlob(
	photoId: string,
	groupId: string,
	transformKey: string,
	maskGroupDigest: string,
	thumbnail: Blob,
) {
	if (typeof URL.createObjectURL !== "function") {
		return false
	}
	try {
		const thumbnailUrl = URL.createObjectURL(thumbnail)
		setMaskGroupThumbnail(photoId, groupId, transformKey, maskGroupDigest, thumbnailUrl)
		return true
	} catch {
		return false
	}
}

export function reserveMaskGroupThumbnails(
	photoId: string,
	groups: { groupId: string; maskGroupDigest: string }[],
	transformKey: string,
) {
	// Reservations deduplicate asynchronous generation without placing thumbnail URLs in history.
	return groups.filter(({ groupId, maskGroupDigest }) => {
		const key = getMaskGroupThumbnailCacheKey(photoId, groupId, transformKey, maskGroupDigest)
		if (pendingMaskGroupThumbnailKeys.has(key) || failedMaskGroupThumbnailKeys.has(key)) {
			return false
		}

		const groupPrefix = `${photoId}:${groupId}:`
		for (const failedKey of failedMaskGroupThumbnailKeys) {
			if (failedKey.startsWith(groupPrefix)) {
				failedMaskGroupThumbnailKeys.delete(failedKey)
			}
		}
		pendingMaskGroupThumbnailKeys.add(key)
		return true
	})
}

export function failMaskGroupThumbnailReservation(
	photoId: string,
	groupId: string,
	transformKey: string,
	maskGroupDigest: string,
) {
	const key = getMaskGroupThumbnailCacheKey(photoId, groupId, transformKey, maskGroupDigest)
	pendingMaskGroupThumbnailKeys.delete(key)
	failedMaskGroupThumbnailKeys.add(key)
}

export function releaseMaskGroupThumbnailReservation(
	photoId: string,
	groupId: string,
	transformKey: string,
	maskGroupDigest: string,
) {
	pendingMaskGroupThumbnailKeys.delete(
		getMaskGroupThumbnailCacheKey(photoId, groupId, transformKey, maskGroupDigest),
	)
}

export function clearFailedMaskGroupThumbnailReservations() {
	failedMaskGroupThumbnailKeys.clear()
}

export function removeMaskGroupThumbnail(photoId: string, groupId: string) {
	const groupPrefix = `${photoId}:${groupId}:`
	for (const key of pendingMaskGroupThumbnailKeys) {
		if (key.startsWith(groupPrefix)) {
			pendingMaskGroupThumbnailKeys.delete(key)
		}
	}
	for (const key of failedMaskGroupThumbnailKeys) {
		if (key.startsWith(groupPrefix)) {
			failedMaskGroupThumbnailKeys.delete(key)
		}
	}

	store.set(maskGroupThumbnailCacheAtom, (cache) => {
		if (cache.photoId !== photoId) {
			return cache
		}
		for (const [key, url] of Object.entries(cache.thumbnails)) {
			if (key.startsWith(groupPrefix)) {
				revokeThumbnailUrl(url)
			}
		}

		return {
			...cache,
			thumbnails: Object.fromEntries(
				Object.entries(cache.thumbnails).filter(([key]) => !key.startsWith(groupPrefix)),
			),
		}
	})
}

export function resetMaskGroupThumbnails() {
	pendingMaskGroupThumbnailKeys.clear()
	failedMaskGroupThumbnailKeys.clear()
	for (const url of Object.values(store.get(maskGroupThumbnailCacheAtom).thumbnails)) {
		revokeThumbnailUrl(url)
	}
	store.set(maskGroupThumbnailCacheAtom, { photoId: null, thumbnails: {} })
}
